/**
 * Grouped log view - displays logs organized by Lambda request ID
 * using the same table structure as flat view for visual consistency.
 */

import React, { useMemo } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { TableCell, TableRow } from '@/components/ui/table';
import type { LogLevel } from '@/types/dashboard';
import type { RequestGroup, LogLevelFilter } from './types';
import { UNGROUPED_REQUEST_ID } from './types';
import { formatTimestamp } from './formatters';
import { LogLevelBadge } from './LogLevelBadge';
import { RequestMetricsBadges } from './MetricsBadges';
import { LogTableShell, LogRow } from './LogTable';
import { useRowExpansion } from './useRowExpansion';
import { getLogEventKey } from './utils';

// =============================================================================
// Group Row Components
// =============================================================================

interface GroupRowProps {
  group: RequestGroup;
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * A table row representing a request group that can expand to show child logs
 */
function GroupRow({ group, isExpanded, onToggle }: GroupRowProps) {
  const groupSeverity = getGroupSeverity(group);
  const isUngrouped = group.requestId === UNGROUPED_REQUEST_ID;

  return (
    <TableRow
      className={`cursor-pointer hover:bg-muted/50 ${getGroupBackgroundClass(group)}`}
      onClick={onToggle}
    >
      <TableCell className="w-[32px] p-2">
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </TableCell>
      <TableCell className="font-mono text-xs whitespace-nowrap">
        {formatTimestamp(group.startTime, 'full')}
      </TableCell>
      <TableCell>
        <LogLevelBadge level={groupSeverity} />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono text-xs">
            {isUngrouped ? 'Ungrouped Logs' : group.requestId}
          </Badge>
          <RequestMetricsBadges
            metrics={group.metrics}
            isColdStart={group.isColdStart}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

interface ExpandedGroupContentProps {
  group: RequestGroup;
  expandedLogs: Set<string>;
  onToggleLog: (key: string) => void;
}

/**
 * Expanded content showing all logs within a request group as expandable rows
 */
function ExpandedGroupContent({
  group,
  expandedLogs,
  onToggleLog,
}: ExpandedGroupContentProps) {
  return (
    <TableRow className="bg-muted/30 hover:bg-muted/30">
      <TableCell colSpan={4} className="p-0">
        <div className="border-l-2 border-muted ml-4">
          <table className="w-full">
            <tbody>
              {group.logs.map((log, index) => {
                const logKey = `${group.requestId}-${getLogEventKey(log, index)}`;
                return (
                  <LogRow
                    key={logKey}
                    event={log}
                    rowKey={logKey}
                    isExpanded={expandedLogs.has(logKey)}
                    onToggle={onToggleLog}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </TableCell>
    </TableRow>
  );
}

// =============================================================================
// Main Component
// =============================================================================

interface GroupedLogViewProps {
  groups: RequestGroup[];
  levelFilter: LogLevelFilter;
}

/**
 * Displays logs grouped by Lambda request ID in a table format
 *
 * Features:
 * - Same table structure as flat view for consistency
 * - Expandable rows for each request group
 * - Child logs within groups are also expandable
 * - Severity shows highest priority level (ERROR > WARNING > INFO)
 * - Execution metrics in group rows
 * - Level filtering within groups
 */
export function GroupedLogView({ groups, levelFilter }: GroupedLogViewProps) {
  const { toggleRow: toggleGroup, isExpanded: isGroupExpanded } =
    useRowExpansion();
  const { toggleRow: toggleLog, expandedRows: expandedLogs } =
    useRowExpansion();

  // Filter logs within groups based on level filter
  const filteredGroups = useMemo(() => {
    if (levelFilter === 'ALL') return groups;

    return groups
      .map((group) => ({
        ...group,
        logs: group.logs.filter((log) => log.level === levelFilter),
      }))
      .filter((group) => group.logs.length > 0);
  }, [groups, levelFilter]);

  if (filteredGroups.length === 0) {
    return (
      <div
        className="h-[300px] flex items-center justify-center text-muted-foreground"
        role="status"
      >
        No {levelFilter.toLowerCase()} logs found
      </div>
    );
  }

  return (
    <LogTableShell>
      {filteredGroups.map((group) => {
        const expanded = isGroupExpanded(group.requestId);

        return (
          <React.Fragment key={group.requestId}>
            <GroupRow
              group={group}
              isExpanded={expanded}
              onToggle={() => toggleGroup(group.requestId)}
            />
            {expanded && (
              <ExpandedGroupContent
                group={group}
                expandedLogs={expandedLogs}
                onToggleLog={toggleLog}
              />
            )}
          </React.Fragment>
        );
      })}
    </LogTableShell>
  );
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Get the highest severity level for a group
 * Priority: ERROR > WARNING > INFO
 */
function getGroupSeverity(group: RequestGroup): LogLevel {
  if (group.hasError) {
    return 'ERROR';
  }
  if (group.hasWarning) {
    return 'WARN';
  }
  return 'INFO';
}

/**
 * Get background class based on group severity
 */
function getGroupBackgroundClass(group: RequestGroup): string {
  if (group.hasError) {
    return 'bg-destructive/5';
  }
  if (group.hasWarning) {
    return 'bg-yellow-500/5';
  }
  return '';
}
